<template>
  <div class="MainLayout flex flex-col min-h-screen h-auto w-full">
    <SiteHeader />
    <div class="flex-auto main-content relative">
      <router-view :key="$route.fullPath"></router-view>
    </div>
    <SiteFooter />
    <ScrollToTopButton />
    <CookiesAgreement class="z-50 fixed bottom-0 w-full" />
    <InfoPopup />
    <MainPopups />
    <NotificationPromptManager />
    <portal-target name="confirm-modal"></portal-target>
    <portal-target name="notifications-panel"></portal-target>
    <portal-target name="file-modal" multiple></portal-target>
  </div>
</template>

<script>
import SiteHeader from '@/components/layout/SiteHeader'
import SiteFooter from '@/components/layout/SiteFooter'
import CookiesAgreement from '@/components/layout/cookies/CookiesAgreement'
import ScrollToTopButton from '@/components/common/ScrollToTopButton'
import InfoPopup from '@/components/common/InfoPopup.vue'
import Notifications from '@/utils/modules/notifications'
import MainPopups from '@/components/popups/MainPopups.vue'
import NotificationPromptManager from '@/components/popups/NotificationPromptManager.vue'
import { syncPushStateOnLaunch } from '@/utils/pushNotifications'
import { setAppBadge } from '@/utils/appBadge'
import { getRegistrationResumeRedirect } from '@/utils/registrationFlow'

export default {
  name: 'MainLayout',
  components: {
    MainPopups,
    NotificationPromptManager,
    InfoPopup,
    ScrollToTopButton,
    CookiesAgreement,
    SiteFooter,
    SiteHeader
  },
  data() {
    return {
      lastConnectionToken: null,
      resumeChecked: false
    }
  },
  computed: {
    user() {
      return this.$store.getters['user/user']
    }
  },
  watch: {
    '$store.state.auth.token': {
      handler(token) {
        if (token) {
          this.initConnection()
        }
      }
    },
    user: {
      async handler() {
        if (!this.$store.state.auth.token) return

        if (this.user.requireRegistration) {
          try {
            await this.$router.push({ name: 'CompleteProfile' })
          } catch {}
          return
        }

        this.checkRegistrationResume()
      }
    }
  },
  created() {
    this.initConnection()
    this.listenServiceWorkerMessages()
    this.syncInitialBadge()
    // The user is usually already loaded by the time the layout mounts (App boot
    // and AuthAccessor both fetch before navigation), so the `user` watcher may
    // never fire. Run the resume check on mount too.
    this.checkRegistrationResume()
  },
  methods: {
    /**
     * LL-169: resume an unfinished registration once per app load. The profile
     * details page is optional, so this only redirects on entry (app open /
     * sign in) and never traps the participant mid-session.
     */
    checkRegistrationResume() {
      if (this.resumeChecked) return
      if (!this.$store.state.auth.token) return
      // Wait until the user is actually loaded (an empty `{}` has no flag).
      if (!this.user || this.user.requireRegistration === undefined) return

      // From here the registration flow owns navigation for this app load. Marking
      // it done before acting also prevents the resume from racing the explicit
      // pushes the registration pages perform (e.g. FirstVisit -> /set-avatar).
      this.resumeChecked = true

      if (this.user.requireRegistration) return

      // Respect explicit deep links / destination navigation restored on sign-in
      // (P5, invitation study/chat): only resume when landing on the dashboard.
      if (this.$route.name !== 'Dashboard' && this.$route.path !== '/') return

      const target = getRegistrationResumeRedirect(this.user)
      if (target && this.$route.name !== target.name) {
        this.$router.replace(target).catch(() => {})
      }
    },
    initConnection() {
      const token = this.$store.state.auth.token
      if (!token || token === this.lastConnectionToken) return
      this.lastConnectionToken = token

      Notifications.connect({
        token: token.replace('Bearer ', '')
      })
      syncPushStateOnLaunch(this.$api, this.$store)
    },
    /**
     * Listen for messages from the service worker (e.g. badge updates
     * triggered by push events while the app is open).
     */
    listenServiceWorkerMessages() {
      if (!('serviceWorker' in navigator)) return

      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SET_APP_BADGE' && typeof event.data.count === 'number') {
          setAppBadge(event.data.count)
        }
      })
    },
    /**
     * Fetch the current unread notification count on app load and sync the
     * Home Screen badge. Covers the case where a push arrived while the app
     * was closed — the badge appears as soon as the app opens or the user
     * taps the notification.
     */
    async syncInitialBadge() {
      const token = this.$store.state.auth.token
      if (!token) return

      try {
        const { unread } = await this.$api.notifications.getMeta()
        setAppBadge(unread)
      } catch {
        // Best-effort — the WS meta event will also set the badge shortly
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.MainLayout {
  .main-content {
    padding-top: var(--header-height);
  }
}
</style>
